import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { DateTime as LuxonDateTime } from 'luxon';

const debug = false;
const debug_removeFalsyProps = false;

const cs = 1000,
	cm = cs * 60,
	ch = cm * 60;

@Injectable()
export class DatesInterceptor implements HttpInterceptor {
	private msJsonDateFormatRegex = /^\/Date\((?:(-?[0-9]+))\)\/$/;
	private jsonTimeFormatRegex = /^\/Time\((?:(-?[0-9]+))\)\/$/;
	private jsonNullDateFormatRegex = /^\/Date\(null\)\/$/;

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const removeUnderscoreUnderscoreProps = (obj: unknown) => {
			if (!obj) {
				return;
			}

			if (typeof obj === 'object') {
				for (const key in obj) {
					if (!Object.prototype.hasOwnProperty.call(obj, key)) {
						continue;
					}

					if (key.startsWith('__')) {
						delete obj[key];
						continue;
					}

					const value = obj[key];
					if (typeof value === 'object') {
						removeUnderscoreUnderscoreProps(value);
					}
				}
			}
		};

		/*
			Clean up the request, removing any falsy values. 
		
			Falsy values are safe to remove because at the Jade end the DTO object loaded 
			from this data will default all its values to null (or equivalent) therefore
			sending a falsy value (blank string, bool false, numeric zero) won't have
			any effect so we might as well save the bandwidth.
		*/
		const removeFalsyProps = (obj: unknown) => {
			if (!obj) {
				return;
			}

			if (typeof obj === 'object') {
				for (const key in obj) {
					if (!Object.prototype.hasOwnProperty.call(obj, key)) {
						continue;
					}

					const value = obj[key];
					const isArray = obj instanceof Array;

					// Remove falsy values - except bool values in arrays.
					if (!value && !(isArray && value === false)) {
						if (debug_removeFalsyProps) console.log('Removing falsy value', key, value);
						delete obj[key];
					} else if (typeof value === 'object') {
						// Exclude pulling apart dates.
						if (value instanceof Date || value instanceof DateTime) {
							// Do nothing
						} else {
							// Recurse
							removeFalsyProps(value);
						}
					}
				}
			}
		};

		const removeTypeMeta = (obj) => {
			if (!obj) {
				return;
			}

			if (obj.$$typeMeta) {
				delete obj.$$typeMeta;
			}

			if (obj.$$hasSecondsMeta) {
				delete obj.$$hasSecondsMeta;
			}

			for (const key in obj) {
				if (!Object.prototype.hasOwnProperty.call(obj, key)) {
					continue;
				}

				const value = obj[key];
				if (typeof value === 'object') {
					removeTypeMeta(value);
				}
			}
		};

		const convertDates = (obj) => {
			for (const key in obj) {
				if (!Object.prototype.hasOwnProperty.call(obj, key)) {
					continue;
				}

				let value = obj[key];
				// Convert Date objects into Microsoft-format date strings.

				if (typeof value === 'object' && (value instanceof DateTime || value?.isLuxonDateTime)) {
					// Convert the DateTime to a native JS Date instance.
					if (typeof value.toJSDate === 'function') {
						value = value.toJSDate();
					} else if (value.ts) {
						value = new Date(value.ts);
					}
				}

				if (typeof value === 'object' && value instanceof Date) {
					let allowSeconds = false;
					if (obj.$$hasSecondsMeta && obj.$$hasSecondsMeta[key]) {
						allowSeconds = true;
					}
					
					if (!allowSeconds) {
						//SCS-61 Timezone issue with times in Ireland
						//If the control is a time picker the date will default to 1899, if the users region is Ireland
						//this will cause and offset by 25m21s causing an error in later logic.						
						if (value.getFullYear() === 1899)
							{
								value.setFullYear(1999);
							}
						
						value.setSeconds(0);
					}

					let msSinceEpoch = value.getTime();

					// Remove the UTC offset so when the server gets it, it can interpret it as a local time.
					const tsOffset = value.getTimezoneOffset() * 60 * 1000;
					msSinceEpoch -= tsOffset;

					const dateValue = value;

					value = `/Date(${msSinceEpoch})/`;
					obj[key] = value;

					if (isDevMode()) {
						// Put a __propName field on the object with the date on it so it's easy to debug.
						obj[`__${key}`] = dateValue;
					}
				} else if (
					(obj.$$typeMeta && obj.$$typeMeta[key] === 'Date' && !value) ||
					// Special date - look for keys staring or ending with date and a null value - if these were created on the client,
					// they won't have a $$typeMeta to be identified as a date.
					(/^date[A-Z]/.test(key) && !value) ||
					(/Date$/.test(key) && !value)
				) {
					// Replace null as a special null Date marker (which gets intercepted at the Jade end).
					value = '/Date(null)/';
					obj[key] = value;
				} else if (typeof value === 'object') {
					convertDates(value);
				} else if (value === '\u0000') {
					obj[key] = '/Char(null)/';
				}
			}
		};

		const responseConvertDates = (obj) => {
			for (const key in obj) {
				if (!Object.prototype.hasOwnProperty.call(obj, key)) {
					continue;
				}

				let value = obj[key];
				let match: RegExpMatchArray | null;
				// Look for Microsoft-format dates and convert to Date objects.
				if (typeof value === 'string' && (match = value.match(this.msJsonDateFormatRegex))) {
					let epochOffset = parseInt(match[1], 10);
					const parsedOffset = epochOffset;
					
					value = new Date(epochOffset);

					// Convert any -4713 year to null.
					if (value.getFullYear() === -4713) {
						value = null;
					} else {
						const tzOffset = value.getTimezoneOffset();
						epochOffset += tzOffset * 60 * 1000;
						value = new Date(epochOffset);
					}

					if (debug) {
						console.log('date: ' + key + ' = ' + value + ' (parsed offset was ' + parsedOffset + ')');
					}

					if (!obj.$$typeMeta) {
						obj.$$typeMeta = {};
					}
					obj[key] = value;
					obj.$$typeMeta[key] = 'Date';

					// Set a flag on any timestamps that have seconds when they came back from the server
					// to allow seconds when they're sent back to the server. (Otherwise they'll be zeroed).
					if (value && value.getSeconds() > 0) {
						if (!obj.$$hasSecondsMeta) {
							obj.$$hasSecondsMeta = {};
						}
						obj.$$hasSecondsMeta[key] = true;
					}
				} else if (typeof value === 'string' && (match = value.match(this.jsonNullDateFormatRegex))) {
					if (!obj.$$typeMeta) {
						obj.$$typeMeta = {};
					}

					// By convention, any property key starting or ending with 'time' will be considered a Time type and a null value
					// actually given a midnight time, rather than null.
					const keyLower = key.toLowerCase();

					if (/^time/.test(keyLower) || /time$/.test(keyLower)) {
						obj[key] = new Date(0, 0, 0, 0, 0, 0, 0);
					} else {
						obj[key] = null;
					}
					obj.$$typeMeta[key] = 'Date';
				} else if (typeof value === 'string' && (match = value.match(this.jsonTimeFormatRegex))) {
					if (!obj.$$typeMeta) {
						obj.$$typeMeta = {};
					}

					value = parseInt(match[1], 10);

					const h = Math.floor(value / ch);
					const m = Math.floor((value - h * ch) / cm);
					const s = Math.floor(value - h * ch - m * cm);
					const ms = Math.floor(value - h * ch - m * cm - s * cs);

					value = new Date(0, 0, 0, h, m, s, ms);

					obj[key] = value;
					obj.$$typeMeta[key] = 'Date';
				} else if (typeof value === 'object') {
					responseConvertDates(value);
				}
			}
		};

		const responseEnsureArraysInitialised = (obj) => {
			for (const key in obj) {
				if (!Object.prototype.hasOwnProperty.call(obj, key)) {
					continue;
				}

				const value = obj[key];

				// Look for keys that start with all followed by a capital letter, or are named "objects".
				// This represents the convention for collection properties from Jade, but excludes
				// properties such as allowOverride, etc.
				if (!value && (/^all[A-Z]/.test(key) || key === 'objects')) {
					obj[key] = [];
				} else if (typeof value === 'object') {
					responseEnsureArraysInitialised(value);
				}
			}
		};

		// Clone the data so we can hack with it and not affect things that are bound to it.
		const req = cloneDeep(request);

		removeUnderscoreUnderscoreProps(req.body);
		removeFalsyProps(req.body);
		convertDates(req.body);
		removeTypeMeta(req.body);

		return next.handle(req).pipe(
			catchError((e) => {
				// Run the date conversion on error responses too as they can contain a legitimate
				// DTO as an alternate response.
				if (e instanceof HttpErrorResponse) {
					responseConvertDates(e);
				}

				return throwError(() => e);
			}),
			tap((evt) => {
				if (evt instanceof HttpResponse) {
					if (evt.body && evt.ok) {
						responseConvertDates(evt.body);
						responseEnsureArraysInitialised(evt.body);
					}
				}
			}),
		);
	}
}
